export const APP_TABS = ['dashboard', 'builder', 'saved', 'library', 'insights', 'chat', 'team', 'account', 'admin'] as const;

export type AppTab = typeof APP_TABS[number];

export type WorkspaceStage = 'overview' | 'targeting' | 'creative' | 'launch' | 'approval';

export interface ResolvedRoute {
  kind: 'marketing' | 'auth' | 'app' | 'admin' | 'review' | 'notFound';
  tab?: AppTab;
  authMode?: 'login' | 'register' | 'demo';
  adminSection?: string;
  campaignId?: string;
  workspaceStage?: WorkspaceStage;
  chatSessionId?: string;
  reviewToken?: string;
  /** الصفحة التسويقية الفرعية (الرئيسية أو صفحات المحتوى الثابت) — مفعّلة فقط لو kind === 'marketing'. */
  marketingPage?: 'home' | 'privacy' | 'terms' | 'contact' | 'faq' | 'about' | 'blog';
  blogSlug?: string;
  blogPreviewId?: string;
  /** لغة الصفحة التسويقية المستنتجة من بادئة الرابط /en — مفعّلة فقط للصفحات التسويقية
   *  اللي بتدعم روابط ثنائية اللغة (SEO/hreflang). غير موجودة لباقي أنواع المسارات. */
  language?: 'ar' | 'en';
}

/** الصفحات التسويقية التي تدعم روابط ثنائية اللغة عن طريق بادئة /en (لأغراض hreflang/SEO). */
export type MarketingPageKey = 'home' | 'privacy' | 'terms' | 'contact' | 'faq' | 'about' | 'blog';

const MARKETING_PATHS: Record<MarketingPageKey, string> = {
  home: '/',
  privacy: '/privacy',
  terms: '/terms',
  contact: '/contact',
  faq: '/faq',
  about: '/about',
  blog: '/blog',
};

/** يبني رابط صفحة تسويقية حسب اللغة: العربي بدون بادئة (الافتراضي)، الإنجليزي ببادئة /en. */
export function pathForMarketing(page: MarketingPageKey, lang: 'ar' | 'en' = 'ar'): string {
  const base = MARKETING_PATHS[page];
  if (lang !== 'en') return base;
  return base === '/' ? '/en' : `/en${base}`;
}

const TAB_PATHS: Record<AppTab, string> = {
  dashboard: '/app',
  builder: '/app/campaigns/new',
  saved: '/app/campaigns',
  library: '/app/library',
  insights: '/app/insights',
  chat: '/app/assistant',
  team: '/app/team',
  account: '/app/account',
  admin: '/admin',
};

const WORKSPACE_STAGES: WorkspaceStage[] = ['overview', 'targeting', 'creative', 'launch', 'approval'];

function cleanPath(pathname: string): string {
  const normalized = `/${String(pathname || '/').split('?')[0].split('#')[0].replace(/^\/+|\/+$/g, '')}`;
  return normalized === '//' ? '/' : normalized;
}

export function pathForTab(tab: AppTab): string {
  return TAB_PATHS[tab];
}

export function pathForCampaign(campaignId: string, stage: WorkspaceStage = 'overview'): string {
  return `/app/campaigns/${encodeURIComponent(campaignId)}/${stage}`;
}

export function pathForAdminSection(section: string): string {
  return !section || section === 'overview' ? '/admin' : `/admin/${encodeURIComponent(section)}`;
}

export function pathForChatSession(sessionId: string): string {
  return `/app/assistant/${encodeURIComponent(sessionId)}`;
}

export function resolveRoute(pathname = window.location.pathname, search = window.location.search): ResolvedRoute {
  let path = cleanPath(pathname);
  const legacyReviewToken = new URLSearchParams(search).get('review');
  if (legacyReviewToken) return { kind: 'review', reviewToken: legacyReviewToken };

  const reviewMatch = path.match(/^\/review\/([^/]+)$/);
  if (reviewMatch) return { kind: 'review', reviewToken: decodeURIComponent(reviewMatch[1]) };

  // بادئة /en — نسخة إنجليزية من الصفحات التسويقية بروابط منفصلة (لدعم hreflang
  // ولإظهار المحتوى الإنجليزي لمحركات البحث). لو موجودة، بنشيلها من المسار قبل
  // المطابقة وبنسجّل اللغة المستنتجة في الـ route نفسه.
  let language: 'ar' | 'en' | undefined;
  if (path === '/en' || path.startsWith('/en/')) {
    language = 'en';
    path = cleanPath(path.slice(3) || '/');
  }

  if (path === '/') return { kind: 'marketing', marketingPage: 'home', language: language || 'ar' };
  if (path === '/privacy') return { kind: 'marketing', marketingPage: 'privacy', language: language || 'ar' };
  if (path === '/terms') return { kind: 'marketing', marketingPage: 'terms', language: language || 'ar' };
  if (path === '/contact') return { kind: 'marketing', marketingPage: 'contact', language: language || 'ar' };
  if (path === '/faq') return { kind: 'marketing', marketingPage: 'faq', language: language || 'ar' };
  if (path === '/about') return { kind: 'marketing', marketingPage: 'about', language: language || 'ar' };
  if (path === '/blog') return { kind: 'marketing', marketingPage: 'blog', language: language || 'ar' };
  const blogMatch = path.match(/^\/blog\/([^/]+)$/);
  if (blogMatch) return { kind: 'marketing', marketingPage: 'blog', blogSlug: decodeURIComponent(blogMatch[1]), language: language || 'ar' };
  const previewMatch = path.match(/^\/blog-preview\/([^/]+)$/);
  if (previewMatch) return { kind: 'marketing', marketingPage: 'blog', blogPreviewId: decodeURIComponent(previewMatch[1]), language: language || 'ar' };
  if (path === '/demo') return { kind: 'marketing', authMode: 'demo', marketingPage: 'home', language: language || 'ar' };
  if (path === '/login') return { kind: 'auth', authMode: 'login' };
  if (path === '/register') return { kind: 'auth', authMode: 'register' };

  const adminMatch = path.match(/^\/admin(?:\/([^/]+))?$/);
  if (adminMatch) {
    return { kind: 'admin', tab: 'admin', adminSection: adminMatch[1] ? decodeURIComponent(adminMatch[1]) : 'overview' };
  }

  const campaignMatch = path.match(/^\/app\/campaigns\/([^/]+)(?:\/([^/]+))?$/);
  if (campaignMatch && campaignMatch[1] !== 'new') {
    const candidate = campaignMatch[2] as WorkspaceStage | undefined;
    return {
      kind: 'app',
      tab: 'builder',
      campaignId: decodeURIComponent(campaignMatch[1]),
      workspaceStage: candidate && WORKSPACE_STAGES.includes(candidate) ? candidate : 'overview',
    };
  }

  const chatMatch = path.match(/^\/app\/assistant\/([^/]+)$/);
  if (chatMatch) return { kind: 'app', tab: 'chat', chatSessionId: decodeURIComponent(chatMatch[1]) };

  if (path === '/app' || path === '/app/dashboard') return { kind: 'app', tab: 'dashboard' };
  if (path === '/app/campaigns/new') return { kind: 'app', tab: 'builder' };
  if (path === '/app/campaigns') return { kind: 'app', tab: 'saved' };
  if (path === '/app/library') return { kind: 'app', tab: 'library' };
  if (path === '/app/insights') return { kind: 'app', tab: 'insights' };
  if (path === '/app/assistant') return { kind: 'app', tab: 'chat' };
  if (path === '/app/team') return { kind: 'app', tab: 'team' };
  if (path === '/app/account') return { kind: 'app', tab: 'account' };

  return { kind: 'notFound', language: language || 'ar' };
}

export function navigate(path: string, replace = false): void {
  const target = cleanPath(path);
  if (`${window.location.pathname}` === target && !window.location.search) return;
  window.history[replace ? 'replaceState' : 'pushState']({}, '', target);
  window.dispatchEvent(new Event('istihdaf:navigation'));
  // React SPA navigation does not reset scroll automatically. Without this, opening
  // login/register from the footer can land halfway down the form on a short iPhone.
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
}
